import type { ReactNode } from 'react'
import { EmptyState, ErrorState, InlineLoader } from 'components/common/Loader'
import { useAsyncData } from 'hooks/useAsyncData'

/**
 * Shared plumbing for every tab: fetch on first open, then render one of
 * loading / error / empty / content. Keeps each panel down to its markup.
 */
export function ListPanel<T>({
  fetcher,
  deps,
  emptyMessage,
  children,
}: {
  fetcher: (signal: AbortSignal) => Promise<T[]>
  deps: unknown[]
  emptyMessage: string
  children: (items: T[]) => ReactNode
}) {
  const { data, loading, error, reload } = useAsyncData(fetcher, deps)

  if (loading) return <InlineLoader />
  if (error) return <ErrorState message={error} onRetry={reload} />
  if (!data || data.length === 0) return <EmptyState message={emptyMessage} />
  return <>{children(data)}</>
}

export function DataTable({
  headers,
  children,
}: {
  headers: string[]
  children: ReactNode
}) {
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}
